import React, { useMemo, useState } from 'react';
import { motion } from 'motion/react';
import { archiveName, dayOf, useSinceMoves, readingNote } from './priceArchive';
import ItemDetailModal from './ItemDetailModal';
import WishlistAddModal from './WishlistAddModal';
import { findSkinImage } from './skinIndex';

// Wishlist 2 — "Watchboard": every wish as a target-hunting card with a
// progress-to-target meter, a glowing TARGET HIT state, sparkline and note.
const CONDITION_MAP = {
  'Factory New':   'Factory New',
  'Minimal Wear':  'Minimal Wear',
  'Field Tested':  'Field-Tested',
  'Well Worn':     'Well-Worn',
  'Battle Scarred':'Battle-Scarred',
};

function getWishPrice(livePrices, item) {
  if (!livePrices || !item.skin) return null;
  if (item.condition) {
    const cond = CONDITION_MAP[item.condition] || item.condition;
    return (livePrices[`${item.skin} (${cond})`] || livePrices[item.skin])?.starting_at?.price ?? null;
  }
  return livePrices[item.skin]?.starting_at?.price ?? null;
}


function Spark({ history }) {
  const prices = (history || []).map(d => d.price).filter(p => p != null);
  if (prices.length < 2) return <span style={{ fontSize: '0.89rem', color: 'var(--text-dim)' }}>collecting…</span>;
  const min = Math.min(...prices), max = Math.max(...prices), span = max - min || 1;
  const w = 110, h = 26;
  const pts = prices.map((p, i) => `${(i / (prices.length - 1) * w).toFixed(1)},${(h - 2 - (p - min) / span * (h - 4)).toFixed(1)}`).join(' ');
  const down = prices[prices.length - 1] <= prices[0];
  return (
    <svg width={w} height={h} style={{ display: 'block', overflow: 'visible' }}>
      <polyline points={pts} fill="none" stroke={down ? '#4ade80' : '#f87171'} strokeWidth="1.5"
        strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

const FILTERS = [
  { key: 'all',     label: 'All' },
  { key: 'hit',     label: 'Target hit' },
  { key: 'waiting', label: 'Waiting' },
];

export default function WishlistTab2({
  wishlist, livePrices, csfloatPrices, skinsData = [],
  itemsWithConditions, onSave, onDelete,
}) {
  const [query, setQuery]   = useState('');
  const [filter, setFilter] = useState('all');
  const [detail, setDetail] = useState(null);   // the wish whose modal is open
  // { initial } while the add/edit modal is open; initial null means adding.
  const [editor, setEditor] = useState(null);

  const items = useMemo(() => {
    return (wishlist || []).map((w, wi) => {
      const price = getWishPrice(livePrices, w);
      const hit   = price != null && w.targetPrice != null && price <= w.targetPrice;
      // Progress toward target: how far price has fallen from its watched high
      const hist  = (w.priceHistory || []).map(d => d.price).filter(p => p != null);
      const high  = hist.length ? Math.max(...hist, price ?? 0) : price;
      let progress = null;
      if (price != null && w.targetPrice != null && high != null && high > w.targetPrice) {
        progress = Math.max(0, Math.min(1, (high - price) / (high - w.targetPrice)));
      } else if (hit) progress = 1;
      const gapPct = price != null && w.targetPrice ? (price - w.targetPrice) / w.targetPrice * 100 : null;
      // Carried on the item so the pricing pass and the render pass agree even
      // though the list is sorted between them and filtered again after that.
      return { ...w, price, hit, progress, gapPct, _k: w.id || `wish${wi}` };
    }).sort((a, b) => (b.hit - a.hit) || ((a.gapPct ?? Infinity) - (b.gapPct ?? Infinity)));
  }, [wishlist, livePrices]);

  // ── Has waiting paid off? ─────────────────────────────────
  // The sparkline only draws days the app was watching, so a wish added six
  // months ago on a machine that was off for five of them shows a flat trail.
  // The archive prices the day the wish was written down, so "it has come down
  // 14% since you started watching" is answerable on the first render.
  const archivePoints = useMemo(() => items.map(w => ({
    key:  w._k,
    name: archiveName(w.skin, w.condition),
    date: dayOf(w.createdAt),
  })), [items]);
  const archive = useSinceMoves(archivePoints, csfloatPrices);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter(w => {
      if (filter === 'hit' && !w.hit) return false;
      if (filter === 'waiting' && w.hit) return false;
      if (q && !w.skin.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [items, query, filter]);

  const hits = items.filter(w => w.hit).length;

  const totals = useMemo(() => {
    const withTarget = items.filter(w => w.targetPrice != null);
    const budget = withTarget.reduce((s, w) => s + w.targetPrice, 0);
    const now    = items.reduce((s, w) => s + (w.price ?? 0), 0);
    const gaps   = withTarget.map(w => w.gapPct).filter(g => g != null);

    // What the whole watchlist has done since each item joined it.
    const drift = items.reduce((acc, w) => {
      const mv = archive.get(w._k);
      if (!mv) return acc;
      return {
        change: acc.change + mv.change,
        base: acc.base + mv.then,
        n: acc.n + 1,
        cheaper: acc.cheaper + (mv.change < 0 ? 1 : 0),
      };
    }, { change: 0, base: 0, n: 0, cheaper: 0 });

    return {
      budget, now, drift,
      driftPct: drift.base > 0 ? (drift.change / drift.base) * 100 : null,
      avgGap: gaps.length ? gaps.reduce((s, g) => s + g, 0) / gaps.length : null,
    };
  }, [items, archive]);

  // Adding and editing are the same modal, so both entry points open it the
  // same way: the empty state below and the button on the toolbar.
  const modal = editor && onSave && (
    <WishlistAddModal
      variant="classic"
      initial={editor.initial}
      wishlist={wishlist || []}
      skinsData={skinsData}
      itemsWithConditions={itemsWithConditions}
      livePrices={livePrices}
      onSave={onSave}
      onDelete={onDelete}
      onClose={() => setEditor(null)}
    />
  );

  if (!items.length) {
    return (
      <div className="wl2-root">
        <div className="lv2-empty">
          {onSave ? (
            <>
              The watchboard is empty. Put a skin on it and its price gets tracked
              from today, owned or not.
              <div style={{ marginTop: 14 }}>
                <button className="lv2-act primary" onClick={() => setEditor({ initial: null })}>
                  Add your first wish
                </button>
              </div>
            </>
          ) : 'The watchboard is empty. Heart an item anywhere in the app to start watching it.'}
        </div>
        {modal}
      </div>
    );
  }

  return (
    <div className="wl2-root">

      {/* ── Headline totals ────────────────────────── */}
      <div className="pt-hero">
        <div className="pt-hero-cell">
          <div className="k">Ready to buy</div>
          <div className="v" style={{ color: hits > 0 ? 'var(--green-dim)' : undefined }}>{hits}</div>
          <div className="s">of {items.length} watched skin{items.length !== 1 ? 's' : ''}</div>
        </div>
        <div className="pt-hero-cell">
          <div className="k">Target budget</div>
          <div className="v">${totals.budget.toFixed(2)}</div>
          <div className="s">cost if every target filled today</div>
        </div>
        <div className="pt-hero-cell">
          <div className="k">Asking now</div>
          <div className="v" style={{ color: 'var(--indigo)' }}>${totals.now.toFixed(2)}</div>
          <div className="s">
            {totals.avgGap != null
              ? `avg ${totals.avgGap >= 0 ? '+' : '−'}${Math.abs(totals.avgGap).toFixed(1)}% above target`
              : 'no live prices yet'}
          </div>
        </div>
        {/* Needs the price archive: the wish's own trail starts the day it was
            added AND the app happened to be running. */}
        {totals.drift.n > 0 && (
          <div className="pt-hero-cell">
            <div className="k">Since you started watching</div>
            <div className="v" style={{ color: totals.drift.change <= 0 ? 'var(--green-dim)' : 'var(--red)' }}>
              {totals.drift.change >= 0 ? '+' : '−'}${Math.abs(totals.drift.change).toFixed(2)}
              {totals.driftPct != null && (
                <span style={{ fontSize: '0.92rem', fontWeight: 700 }}>
                  {' '}({totals.driftPct >= 0 ? '+' : '−'}{Math.abs(totals.driftPct).toFixed(1)}%)
                </span>
              )}
            </div>
            <div className="s">
              {totals.drift.cheaper} of {totals.drift.n} priced are cheaper than when you added them
            </div>
          </div>
        )}
      </div>

      {/* ── Toolbar ────────────────────────────────── */}
      <div className="lv2-bar" style={{ position: 'static', marginBottom: 16 }}>
        <div className="lv2-search">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="6.5" /><path d="M16 16l4 4" />
          </svg>
          <input placeholder="Search watchboard…" value={query} onChange={e => setQuery(e.target.value)} />
        </div>
        {FILTERS.map(f => (
          <button key={f.key} className={`lv2-chip ${filter === f.key ? 'on' : ''}`} onClick={() => setFilter(f.key)}>
            {f.label}{f.key === 'hit' && hits > 0 ? ` (${hits})` : ''}
          </button>
        ))}
        <div className="lv2-actions" style={{ marginLeft: 'auto' }}>
          <span style={{ fontSize: '0.92rem', color: 'var(--text-dim)', fontWeight: 600 }}>
            {items.length} watched · {hits} at target
          </span>
          {onSave && (
            <button className="lv2-act primary" onClick={() => setEditor({ initial: null })}
              title="Watch any skin, whether you own it or not">
              + Add item
            </button>
          )}
        </div>
      </div>

      {/* ── Cards ──────────────────────────────────── */}
      {filtered.length === 0 ? (
        <div className="lv2-empty">Nothing matches your filters.</div>
      ) : (
        <div className="wl2-grid">
          {filtered.map((w, i) => {
            const img = findSkinImage(skinsData, w.skin, w.condition);
            return (
              <motion.div key={w._k} className={`wl2-card click ${w.hit ? 'hit' : ''}`}
                initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1], delay: Math.min(i, 16) * 0.04 }}
                role="button" tabIndex={0} title="Open details"
                onClick={() => setDetail({ ...w, img })}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDetail({ ...w, img }); } }}>

                {w.hit && <span className="wl2-hit-badge">TARGET HIT</span>}

                <div className="wl2-img-row">
                  {img
                    ? <img src={img} alt="" />
                    : <div style={{ width: 74, height: 48, background: 'rgba(255,255,255,0.04)', borderRadius: 8, flexShrink: 0 }} />}
                  <div style={{ minWidth: 0 }}>
                    <div className="wl2-name" title={w.skin}>{w.skin}</div>
                    <div className="wl2-cond">
                      {w.condition || 'Any condition'} · watching since{' '}
                      {w.createdAt ? new Date(w.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
                    </div>
                  </div>
                </div>

                <div className="wl2-prices">
                  <span className="cur">{w.price != null ? `$${w.price.toFixed(2)}` : '—'}</span>
                  <span className="tgt">{w.targetPrice != null ? `target $${w.targetPrice.toFixed(2)}` : 'no target set'}</span>
                </div>

                {w.progress != null && (
                  <>
                    <div className="wl2-meter">
                      <motion.div className="fill"
                        style={{ background: w.hit ? 'var(--green-dim)' : 'linear-gradient(90deg, var(--indigo), var(--green-dim))' }}
                        initial={{ width: 0 }}
                        animate={{ width: `${Math.round(w.progress * 100)}%` }}
                        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.2 + Math.min(i, 16) * 0.04 }}
                      />
                    </div>
                    <div className="wl2-gap" style={{ color: w.hit ? 'var(--green-dim)' : 'var(--text-muted)' }}>
                      {w.hit
                        ? `Below target — ${Math.abs(w.gapPct).toFixed(1)}% under. Time to buy?`
                        : w.gapPct != null
                          ? `${w.gapPct.toFixed(1)}% above target`
                          : ''}
                    </div>
                  </>
                )}

                {/* What it has actually done since you started watching. The
                    trail beside it only covers days this app was running; this
                    covers the whole wait. Green means it has come toward you. */}
                {(() => {
                  const mv = archive.get(w._k);
                  if (!mv) return null;
                  return (
                    <div className="wl2-gap" title={readingNote(mv)}
                      style={{ color: mv.change <= 0 ? 'var(--green-dim)' : 'var(--text-muted)' }}>
                      {mv.change <= 0 ? 'Down' : 'Up'} {Math.abs(mv.pct).toFixed(1)}% since you added it
                      {' '}· was ${mv.then.toFixed(2)}
                    </div>
                  );
                })()}

                <div className="wl2-spark">
                  <span className="lbl">Price trail</span>
                  <Spark history={w.priceHistory} />
                  {/* Sits in the footer rather than the corner, which the
                      TARGET HIT badge owns. Stops the click reaching the card,
                      whose own job is opening the detail modal. */}
                  {onSave && (
                    <button className="wl2-edit" title="Edit target, note or wear"
                      onClick={e => { e.stopPropagation(); setEditor({ initial: w }); }}>
                      Edit
                    </button>
                  )}
                </div>

                {w.note && <div className="wl2-note" title={w.note}>“{w.note}”</div>}
              </motion.div>
            );
          })}
        </div>
      )}

      {detail && (
        <ItemDetailModal
          variant="classic"
          kind="wish"
          name={detail.skin}
          condition={detail.condition}
          image={detail.img}
          history={detail.priceHistory}
          livePrices={livePrices}
          csfloatPrices={csfloatPrices}
          targetPrice={detail.targetPrice}
          note={detail.note}
          createdAt={detail.createdAt}
          onClose={() => setDetail(null)}
        />
      )}

      {modal}
    </div>
  );
}
